import { CEFR_LEVELS, type CefrLevel } from "@rmnlng/shared";
import { Check, ChevronRight, Flame, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useT, type I18nKey } from "@/lib/i18n";
import { useProgressLevels, useProgressStats, type LevelProgress, type ProgressStats } from "@/lib/progress";
import { cn } from "@/lib/utils";

// Mirrors the constant in Levels.tsx — kept duplicated rather than
// hoisted so each page is self-contained. If it ever drifts, design
// review should catch it (both pages use vocab vs grammar bars).
const GRAMMAR_COLOR = "oklch(58% 0.12 150)";

function levelNameKey(level: CefrLevel): I18nKey {
  return `level.name_${level}` as I18nKey;
}

export function ProgressPage() {
  const { data, isPending, isError, error } = useProgressStats();
  const t = useT();

  if (isPending) {
    return <ProgressPageSkeleton />;
  }
  if (isError || !data) {
    return (
      <p className="text-[13px] text-[color:var(--again)]">
        {error instanceof Error ? error.message : t("common.failed_to_load")}
      </p>
    );
  }

  const wordsNew = Math.max(0, data.wordsTotalRo - data.wordsLearning);
  const wordsLearningOnly = Math.max(0, data.wordsLearning - data.wordsMature);

  return (
    <div className="flex flex-col gap-[14px]">
      <div>
        <h1 className="text-[26px] font-bold tracking-[-0.02em]">{t("progress.title")}</h1>
        <div className="mt-0.5 text-[13px] text-text-3">{t("progress.subtitle_all_time")}</div>
      </div>

      {/* Per-level snapshot — current-level hero + 7-pill strip.
          Sits at the top because "where am I in the path?" is the
          first question a returning user has; all-time totals sit
          below as supporting context. */}
      <ByLevelSection />

      {/* 2×2 hero stats */}
      <div className="grid grid-cols-2 gap-2.5">
        <div className="stat-tile">
          <div className="n accent">{data.wordsMature}</div>
          <div className="lbl">{t("progress.stat_words_known")}</div>
          <div className="delta">
            {data.reviewsThisWeek > 0
              ? t("progress.delta_this_week", { n: data.reviewsThisWeek })
              : t("progress.delta_none")}
          </div>
        </div>

        <div className="stat-tile">
          <div className="n">{data.wordsLearning}</div>
          <div className="lbl">{t("progress.stat_learning")}</div>
          <div className="delta">
            {data.avgEase > 0 ? t("progress.delta_avg_ease", { n: data.avgEase.toFixed(1) }) : t("progress.delta_none")}
          </div>
        </div>

        <div className="stat-tile">
          <div className="n flex items-center gap-1.5" style={{ color: "var(--flame)" }}>
            <Flame className="size-[22px]" />
            {data.streakDays}
          </div>
          <div className="lbl">{t("progress.stat_day_streak")}</div>
          <div className="delta">
            {data.bestStreak > 0 ? t("progress.delta_best_streak", { n: data.bestStreak }) : t("progress.delta_none")}
          </div>
        </div>

        <div className="stat-tile">
          <div className="n">{data.retentionPct}%</div>
          <div className="lbl">{t("progress.stat_retention")}</div>
          <div className="delta">{t("progress.delta_last_30d")}</div>
        </div>
      </div>

      {/* Vocabulary breakdown */}
      <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <div className="text-[13px] font-semibold">{t("progress.vocabulary")}</div>
          <div className="font-mono text-[12px] text-text-3">{t("progress.total_words", { n: data.wordsTotalRo })}</div>
        </div>
        <VocabBar mature={data.wordsMature} learning={wordsLearningOnly} unseen={wordsNew} />
        <div className="mt-2.5 flex flex-wrap justify-between gap-2 text-[12px]">
          <Legend dotClass="bg-[color:var(--accent)]" label={t("progress.legend_known")} value={data.wordsMature} />
          <Legend
            dotClass="bg-[color:var(--accent-soft)]"
            label={t("progress.legend_learning")}
            value={wordsLearningOnly}
          />
          <Legend dotClass="bg-[color:var(--surface-3)]" label={t("progress.legend_new")} value={wordsNew} />
        </div>
      </div>

      {/* Activity heatmap */}
      <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <div className="text-[13px] font-semibold">{t("progress.activity")}</div>
          <div className="text-[12px] text-text-3">{t("progress.last_13_weeks")}</div>
        </div>
        <Heatmap data={data.reviewsPerDay} />
        <div className="mt-2.5 flex items-center justify-end gap-1 text-[11px] text-text-3">
          <span>{t("progress.heatmap_less")}</span>
          <span className="size-[10px] rounded-[2px] bg-[color:var(--surface-3)]" />
          <span
            className="size-[10px] rounded-[2px]"
            style={{
              background: "color-mix(in srgb, var(--accent) 30%, var(--surface-3))",
            }}
          />
          <span
            className="size-[10px] rounded-[2px]"
            style={{
              background: "color-mix(in srgb, var(--accent) 60%, var(--surface-3))",
            }}
          />
          <span className="size-[10px] rounded-[2px] bg-[color:var(--accent)]" />
          <span>{t("progress.heatmap_more")}</span>
        </div>
      </div>
    </div>
  );
}

// ─── By-level section ──────────────────────────────────────────
//
// Two visual elements:
//   1. Hero card for the user's current level — name, vocab/grammar
//      bars, "Готов к экзамену" badge if both rings ≥ 85%, and a
//      View → link to /levels/{X} for the deep-dive roadmap view.
//   2. A 7-pill horizontal strip for ALL CEFR levels: tap to jump.
//      Visual state encodes status (done check / active dot / locked
//      icon) without needing extra labels. Compact enough to fit on
//      mobile (375px) without scrolling.
//
// Renders nothing while the levels query is in-flight — the section
// just collapses, leaving the all-time stats below in place. Same on
// error: a failed levels query shouldn't break the rest of the page.

function ByLevelSection() {
  const { data, isPending, isError } = useProgressLevels();
  const t = useT();

  if (isPending) return <ByLevelSkeleton />;
  if (isError || !data) return null;

  const current = data.levels.find((l) => l.level === data.currentLevel);
  if (!current) return null;

  return (
    <section className="flex flex-col gap-2.5">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("progress.section_by_level")}
      </div>
      <CurrentLevelHero current={current} />
      <LevelStrip levels={data.levels} currentLevel={data.currentLevel} />
    </section>
  );
}

function CurrentLevelHero({ current }: { current: LevelProgress }) {
  const t = useT();
  return (
    <Link
      to={`/levels/${current.level}`}
      className="flex flex-col gap-3 rounded-[var(--r-lg)] border border-border bg-surface p-4 transition-colors hover:bg-[color:var(--surface-2)]"
    >
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="text-[13px] font-semibold">
            {current.level} · {t(levelNameKey(current.level))}
          </div>
          {current.readyForExam && (
            <div className="mt-0.5 text-[12px]" style={{ color: "var(--good)" }}>
              {t("home.ready_for_exam")}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 text-[12px] font-medium text-text-3">
          {t("progress.view_level")}
          <ChevronRight className="size-3.5" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <ProgressBar label={t("level_hub.bar_vocab")} pct={current.vocab.pct} color="var(--accent)" />
        <ProgressBar label={t("level_hub.bar_grammar")} pct={current.grammar.pct} color={GRAMMAR_COLOR} />
      </div>
    </Link>
  );
}

function LevelStrip({ levels, currentLevel }: { levels: LevelProgress[]; currentLevel: CefrLevel }) {
  // CEFR_LEVELS drives the rendering order so levels missing from the
  // API response (shouldn't happen — endpoint returns all 7 — but
  // defensive) just fall through with default "locked" treatment.
  const byLevel = new Map<CefrLevel, LevelProgress>(levels.map((l) => [l.level, l]));
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {CEFR_LEVELS.map((level) => {
        const lp = byLevel.get(level);
        const status = lp?.status ?? "locked";
        const isCurrent = level === currentLevel;
        return (
          <Link
            key={level}
            to={`/levels/${level}`}
            className={cn(
              "flex flex-col items-center gap-0.5 rounded-[var(--r-md)] border px-1 py-2 transition-colors",
              isCurrent
                ? "border-[color:var(--accent)] bg-[color:var(--accent-soft)]"
                : "border-border bg-surface hover:bg-[color:var(--surface-2)]",
            )}
          >
            <span
              className={cn(
                "font-mono text-[12px] font-semibold",
                isCurrent ? "text-[color:var(--accent)]" : "text-text-2",
              )}
            >
              {level}
            </span>
            <LevelStatusIcon status={status} isCurrent={isCurrent} />
          </Link>
        );
      })}
    </div>
  );
}

function LevelStatusIcon({ status, isCurrent }: { status: LevelProgress["status"]; isCurrent: boolean }) {
  // Visual hierarchy:
  //   done    → ✓ in success color (the user has cleared the level)
  //   active  → solid dot in accent (user's current focus)
  //   next    → outlined dot in muted color (immediately upcoming)
  //   locked  → padlock icon (further out, not actionable yet)
  if (status === "done") {
    return <Check className="size-3" style={{ color: "var(--good)" }} />;
  }
  if (status === "active" || isCurrent) {
    return <span className="size-1.5 rounded-full bg-[color:var(--accent)]" />;
  }
  if (status === "next") {
    return <span className="size-1.5 rounded-full border border-[color:var(--text-3)]" />;
  }
  return <Lock className="size-3 text-text-3" />;
}

function ProgressBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div className="flex-1">
      <div className="mb-0.5 flex justify-between font-mono text-[10px] text-text-3">
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div className="progress-track" style={{ height: 4 }}>
        <div className="fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function ByLevelSkeleton() {
  return (
    <section className="flex flex-col gap-2.5">
      <Skeleton className="h-3 w-24" />
      <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
        <Skeleton className="h-4 w-40" />
        <div className="mt-3 flex items-center gap-3">
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 flex-1" />
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-12 rounded-[var(--r-md)]" />
        ))}
      </div>
    </section>
  );
}

function VocabBar({ mature, learning, unseen }: { mature: number; learning: number; unseen: number }) {
  const total = mature + learning + unseen;
  if (total === 0) {
    return <div className="h-2.5 rounded-full" style={{ background: "var(--surface-3)" }} />;
  }
  const pct = (n: number) => (n / total) * 100;
  return (
    <div className="flex h-2.5 overflow-hidden rounded-full" style={{ background: "var(--surface-3)" }}>
      <div
        className="h-full"
        style={{
          width: `${pct(mature)}%`,
          background: "var(--accent)",
        }}
      />
      <div
        className="h-full"
        style={{
          width: `${pct(learning)}%`,
          background: "var(--accent-soft)",
          borderLeft: "1px solid var(--surface)",
        }}
      />
    </div>
  );
}

function Legend({ dotClass, label, value }: { dotClass: string; label: string; value: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={cn("size-2 rounded-[2px]", dotClass)} />
      <span className="text-text-2">{label}</span>
      <span className="font-mono text-text-3">{value}</span>
    </div>
  );
}

function Heatmap({ data }: { data: ProgressStats["reviewsPerDay"] }) {
  return (
    <div className="heatmap">
      {data.map((d) => {
        const lv = heatmapBucket(d.count);
        return <div key={d.date} className={cn("cell", lv > 0 && `l${lv}`)} title={`${d.date}: ${d.count}`} />;
      })}
    </div>
  );
}

// Renamed from `level` so it doesn't shadow CEFR-level variable names
// elsewhere in this file. Returns a 0–3 intensity bucket for one day's
// review count; the consumer maps that to a CSS class (`l1`/`l2`/`l3`).
function heatmapBucket(count: number): 0 | 1 | 2 | 3 {
  if (count === 0) return 0;
  if (count <= 2) return 1;
  if (count <= 5) return 2;
  return 3;
}

function ProgressPageSkeleton() {
  const t = useT();
  return (
    <div className="flex flex-col gap-[14px]">
      <div>
        <h1 className="text-[26px] font-bold tracking-[-0.02em]">{t("progress.title")}</h1>
        <div className="mt-0.5 text-[13px] text-text-3">{t("progress.subtitle_all_time")}</div>
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="stat-tile">
            <Skeleton className="mb-1.5 h-8 w-16" />
            <Skeleton className="mb-1.5 h-3 w-20" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>

      <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-2.5 w-full rounded-full" />
        <div className="mt-2.5 flex flex-wrap justify-between gap-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>

      <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-[84px] w-full" />
      </div>
    </div>
  );
}
